import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DecimalField from '../shared/DecimalField';
import { formatPrice, roundMoney } from '../../utils/vat';

const DEFAULT_VAT_RATE = 25.5;

function OrderDiscountDialog({ open, handleClose, maintenance, setMaintenance }) {
  const { t } = useTranslation();

  const [percent, setPercent] = useState('');
  const [amount, setAmount] = useState('');
  const [target, setTarget] = useState('');
  const [activeField, setActiveField] = useState(null);
  const [saving, setSaving] = useState(false);

  const subtotalExclVat = parseFloat(maintenance?.subtotal_excl_vat || 0);
  const subtotalInclVat = (maintenance?.parts || []).reduce(
    (sum, p) => sum + (parseFloat(p.line_total_incl_vat) || parseFloat(p.line_total) || 0), 0
  ) + (maintenance?.jobs || []).reduce(
    (sum, j) => sum + (parseFloat(j.line_total_incl_vat) || parseFloat(j.line_total) || 0), 0
  ) || roundMoney(subtotalExclVat * (1 + DEFAULT_VAT_RATE / 100));

  const hasSavedDiscount = parseFloat(maintenance?.discount_percent || 0) > 0
    || parseFloat(maintenance?.discount_amount || 0) > 0;

  // Convert incl-VAT amount to excl-VAT for backend storage using weighted average VAT rate
  const toExclVat = (amountIncl) => {
    if (subtotalInclVat > 0 && subtotalExclVat > 0) {
      return roundMoney(amountIncl * subtotalExclVat / subtotalInclVat);
    }
    return roundMoney(amountIncl / (1 + DEFAULT_VAT_RATE / 100));
  };

  useEffect(() => {
    if (!open) return;
    const savedTarget = parseFloat(maintenance?.target_total_incl_vat || 0);
    const dp = parseFloat(maintenance?.discount_percent || 0);
    const da = parseFloat(maintenance?.discount_amount || 0);
    if (savedTarget > 0 && maintenance?.target_total_incl_vat != null) {
      setTarget(String(savedTarget));
      const amountIncl = roundMoney(subtotalInclVat - savedTarget);
      setAmount(String(amountIncl));
      setPercent(subtotalInclVat > 0 ? String(roundMoney(amountIncl / subtotalInclVat * 100)) : '');
      setActiveField('target');
    } else if (dp > 0) {
      setPercent(String(dp));
      const amountIncl = roundMoney(subtotalInclVat * dp / 100);
      setAmount(String(amountIncl));
      setTarget(String(roundMoney(subtotalInclVat - amountIncl)));
      setActiveField('percent');
    } else if (da > 0) {
      // da is excl-VAT from DB — use order_discount_total (incl-VAT) from API if available
      const apiIncl = maintenance?.order_discount_total;
      const amountIncl = apiIncl != null && parseFloat(apiIncl) > 0
        ? parseFloat(apiIncl)
        : roundMoney(da * subtotalInclVat / (subtotalExclVat || 1));
      setAmount(String(roundMoney(amountIncl)));
      setPercent(subtotalInclVat > 0 ? String(roundMoney(amountIncl / subtotalInclVat * 100)) : '');
      setTarget(String(roundMoney(subtotalInclVat - amountIncl)));
      setActiveField('amount');
    } else {
      setPercent('');
      setAmount('');
      setTarget('');
      setActiveField(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePercentChange = (val) => {
    const num = parseFloat(val);
    if (!isNaN(num) && num > 100) return;
    setPercent(val);
    if (val === '' || val === '0') {
      setAmount('');
      setTarget('');
      return;
    }
    const p = parseFloat(val) || 0;
    const amountIncl = roundMoney(subtotalInclVat * p / 100);
    setAmount(String(amountIncl));
    setTarget(String(roundMoney(subtotalInclVat - amountIncl)));
  };

  const handleAmountChange = (val) => {
    const num = parseFloat(val);
    if (!isNaN(num) && num < 0) return;
    setAmount(val);
    if (val === '' || val === '0') {
      setPercent('');
      setTarget('');
      return;
    }
    const a = parseFloat(val) || 0;
    setPercent(subtotalInclVat > 0 ? String(roundMoney(a / subtotalInclVat * 100)) : '');
    setTarget(String(roundMoney(subtotalInclVat - a)));
  };

  const handleTargetChange = (val) => {
    setTarget(val);
    if (val === '' || val === '0') {
      setPercent('');
      setAmount('');
      return;
    }
    const t = parseFloat(val) || 0;
    const amountIncl = roundMoney(subtotalInclVat - t);
    setAmount(String(amountIncl));
    setPercent(subtotalInclVat > 0 ? String(roundMoney(amountIncl / subtotalInclVat * 100)) : '');
  };

  const currentAmountIncl = parseFloat(amount) || 0;
  const targetNum = parseFloat(target) || 0;
  const previewDiscount = roundMoney(currentAmountIncl);
  // When target is active, show the exact typed value as total
  const previewTotal = activeField === 'target' && targetNum > 0
    ? roundMoney(targetNum)
    : roundMoney(subtotalInclVat - previewDiscount);
  const targetExceedsSubtotal = parseFloat(target) > subtotalInclVat && activeField === 'target';
  const isValid = currentAmountIncl >= 0 && !targetExceedsSubtotal;

  const handleSave = async () => {
    setSaving(true);
    try {
      const amountIncl = parseFloat(amount) || 0;
      const exclAmount = String(toExclVat(amountIncl));
      let payload;
      if (activeField === 'percent') {
        // % mode: no target — accept minor rounding
        payload = { discount_percent: percent || '0', discount_amount: '0', target_total_incl_vat: null };
      } else if (activeField === 'target') {
        // Target mode: exact total
        payload = { discount_amount: exclAmount, discount_percent: '0', target_total_incl_vat: target };
      } else {
        // Amount mode: set target for exact incl-VAT display
        const targetVal = String(roundMoney(subtotalInclVat - amountIncl));
        payload = { discount_amount: exclAmount, discount_percent: '0', target_total_incl_vat: targetVal };
      }
      const res = await axios.patch(`/api/maintenances/${maintenance.id}/set_discount/`, payload);
      setMaintenance(res.data);
      handleClose();
    } catch {
      // Let caller handle via snackbar — close dialog anyway so it's not stuck
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`/api/maintenances/${maintenance.id}/set_discount/`, {
        discount_percent: '0',
        discount_amount: '0',
        target_total_incl_vat: null,
      });
      setMaintenance(res.data);
      handleClose();
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  const computedStyle = { color: 'text.secondary' };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('orderDiscount')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, mt: 0.5 }}>
          {t('subtotal')} ({t('incl. VAT')}): {formatPrice(subtotalInclVat)} &euro;
        </Typography>

        <Box sx={{ mb: 2 }}>
          <DecimalField
            label={t('discountPercent')}
            value={percent}
            onChange={handlePercentChange}
            onFocus={() => setActiveField('percent')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              sx: activeField !== 'percent' && percent ? computedStyle : {},
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <DecimalField
            label={t('discountAmount')}
            value={amount}
            onChange={handleAmountChange}
            onFocus={() => setActiveField('amount')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">&euro;</InputAdornment>,
              sx: activeField !== 'amount' && amount ? computedStyle : {},
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <DecimalField
            label={t('targetTotal')}
            value={target}
            onChange={handleTargetChange}
            onFocus={() => setActiveField('target')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">&euro;</InputAdornment>,
              sx: activeField !== 'target' && target ? computedStyle : {},
            }}
            helperText={targetExceedsSubtotal ? t('targetExceedsSubtotal') : undefined}
            error={targetExceedsSubtotal}
          />
        </Box>

        {/* Live preview */}
        {currentAmountIncl > 0 && (
          <Box sx={{ bgcolor: 'grey.50', borderRadius: '8px', p: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{t('subtotal')}:</Typography>
              <Typography variant="body2" color="text.secondary">{formatPrice(subtotalInclVat)} &euro;</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#16a34a' }}>{t('orderDiscount')}:</Typography>
              <Typography variant="body2" sx={{ color: '#16a34a' }}>&minus;{formatPrice(previewDiscount)} &euro;</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={600}>{t('Total')}:</Typography>
              <Typography variant="body2" fontWeight={600}>{formatPrice(previewTotal)} &euro;</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box>
          {hasSavedDiscount && (
            <Button color="error" onClick={handleRemove} disabled={saving}>
              {t('removeDiscount')}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={saving}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !isValid}
          >
            {t('Save')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default OrderDiscountDialog;
